import React, { useEffect, useState } from 'react';
import { Row, Col, Card, CardBody, Modal } from 'reactstrap';
import { get, post, del, put } from 'helpers/api_helper';
import Datatables from 'pages/utils/table/datatable';
import { ManageTreatmentsColumns } from './tableColumns';
import { TREATMENT_TYPE_URL } from 'helpers/url_helper';
import { showSuccessAlert, showConfirmAlert } from "pages/utils/alertMessages";
import { useTranslation } from "react-i18next";

const ManageTreatments = () => {
    const { t } = useTranslation();
    const [isForm, setIsForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({});
    const [rows, setRows] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, Search: '' });

    const fetchData = async () => {
        setLoading(true);
        const { success, body } = await get(`${TREATMENT_TYPE_URL}?page=${pagination.page}&limit=${pagination.limit}&search=${pagination.Search}`);
        if (success) {
            setRows(body);
            setLoading(false);
        }
    }

    useEffect(fetchData, [pagination]);

    const handleToggle = () => {
        setIsForm(!isForm);
        setEditMode(false);
        setEditingId(null);
        setFormData({});
        setLoading(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        const url = editMode ? `${TREATMENT_TYPE_URL}/${editingId}` : TREATMENT_TYPE_URL;

        const { success, body } = editMode ? await put(url, formData) : await post(url, formData);

        if (success) {
            showSuccessAlert(editMode ? t('Treatment updated successfully!') : t('Treatment created successfully!'));
            setIsForm(false);
            setEditMode(false);
            setEditingId(null);
            setFormData({});
            fetchData();
        }
    };

    const handleEdit = (row) => {
        setFormData(row);
        setEditMode(true);
        setEditingId(row.id);
        setIsForm(true);
        setLoading(false);
    };

    const handleDelete = async (id, sts) => {
        const action = sts === 1 ? t('De-activate') : t('Activate');

        const { isConfirmed } = await showConfirmAlert(`${action} ${t('Treatment')}`, `${t('Do you really want to')} ${action} ${t('this Treatment')}?`,
            `${t('Yes')}, ${action} ${t('it')}!`, `${action}d!`, `${t('Treatment has been updated successfully.')}`);

        if (isConfirmed) {
            const { success } = await del(`${TREATMENT_TYPE_URL}/${id}?sts=${sts}`);
            if (success) {
                fetchData();
            }
        }
    };

    return (
        <React.Fragment>
            <Card>
                <CardBody>
                    <Datatables
                        isSearch={true}
                        columns={ManageTreatmentsColumns({ "edit": handleEdit, "delete": handleDelete }, pagination)}
                        showTableOnly={true}
                        rows={rows.items || []}
                        rowsLength={rows?.totalItems || 0}
                        rowsPerPage={pagination.limit}
                        keyField={'id'}
                        handleAddButton={handleToggle}
                        title={t("All Clinical Treatments")}
                        isAdd={true}
                        isTableHead={true}
                        loading={loading}
                        ssr={setPagination}
                    />
                </CardBody>
            </Card>

            <Modal isOpen={isForm} toggle={handleToggle} size="xl" centered>
                <div className="modal-header">
                    <h5 className="modal-title font-weight-bold">
                        {editMode ? t('Update Clinical Treatment') : t('Add New Clinical Treatment')}
                    </h5>
                    <button type="button" className="btn-close" onClick={handleToggle} aria-label="Close" />
                </div>
                <div className="modal-body p-4">
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="mb-2">
                                <label className="form-label font-weight-semibold">{t("Treatment Title *")}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t("e.g. Root Canal Treatment (RCT)")}
                                    name="title"
                                    value={formData.title || ''}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-2">
                                <label className="form-label font-weight-semibold">{t("Treatment Cost (₹) *")}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t("e.g. 2500")}
                                    name="cost"
                                    value={formData.cost || ''}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-danger" onClick={handleToggle}>
                        {t("Cancel")}
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? t('Processing...') : (editMode ? t('Update Treatment') : t('Submit Treatment'))}
                    </button>
                </div>
            </Modal>
        </React.Fragment>
    );
};

export default ManageTreatments;
